// Registro unico de puntos de ayuda del backoffice.
//
// Cada `data-help="<id>"` del portal se resuelve aca, y de aca sale la URL del
// manual que se abre. Un id que no figure en este mapa no abre nada: por eso
// `npm run admin-docs:check` compara las dos listas — los `data-help` del
// codigo contra las claves de este objeto — y ademas verifica que la pagina y
// el ancla existan en el manual, en los dos idiomas.
//
// Las anclas son EXPLICITAS (`{#ancla}` en el MDX) y no el slug del titulo. El
// manual es bilingue: si el ancla saliera del titulo, el mismo boton llevaria a
// `#buscar-un-usuario` en español y a `#search-for-a-user` en ingles, y el
// enlace profundo dejaria de funcionar apenas alguien cambia de idioma.

export interface HelpTarget {
  /** Ruta de la pagina del manual, sin idioma ni barras. */
  page: string;
  /** Ancla explicita dentro de esa pagina. Sin ancla, abre arriba de todo. */
  anchor?: string;
  /** Nombre del elemento. Se muestra al señalarlo, antes de abrir el manual. */
  label: string;
}

export const HELP_TARGETS = {
  // --- Marco de la aplicacion ---------------------------------------------
  'shell.brand': { page: 'introduccion', label: 'Backoffice de AuthSys' },
  'shell.theme': {
    page: 'primeros-pasos/interfaz',
    anchor: 'tema',
    label: 'Tema claro / oscuro',
  },
  'shell.help': {
    page: 'primeros-pasos/ayuda',
    label: 'Botón de ayuda',
  },
  'shell.user-menu': {
    page: 'primeros-pasos/interfaz',
    anchor: 'menu-de-cuenta',
    label: 'Menú de tu cuenta',
  },
  'shell.logout': {
    page: 'primeros-pasos/ingresar',
    anchor: 'cerrar-sesion',
    label: 'Cerrar sesión',
  },
  'shell.version': {
    page: 'primeros-pasos/interfaz',
    anchor: 'version',
    label: 'Versión publicada',
  },
  'shell.nav': {
    page: 'primeros-pasos/interfaz',
    anchor: 'menu-lateral',
    label: 'Menú lateral',
  },
  'shell.nav.dashboard': { page: 'panel', label: 'Panel' },
  'shell.nav.users': { page: 'usuarios/buscar', label: 'Usuarios' },
  'shell.nav.charges': { page: 'usuarios/cobros', label: 'Cobros pendientes' },
  'shell.nav.partners': { page: 'partners/lista', label: 'Partners' },
  'shell.nav.catalog': { page: 'catalogo/tipos', label: 'Catálogo' },
  'shell.nav.reports': { page: 'reportes', label: 'Reportes' },
  'shell.nav.invoices': { page: 'facturas', label: 'Facturas' },
  'shell.nav.jobs': { page: 'trabajos', label: 'Jobs y DLQ' },
  'shell.nav.audit': { page: 'auditoria', label: 'Registro de auditoría' },
  'shell.nav.disposable': { page: 'blacklist-email', label: 'Blacklist de email' },
  'shell.nav.team': { page: 'equipo', label: 'Equipo' },
  'shell.nav.coupons': { page: 'cupones', label: 'Cupones' },

  // --- Ingreso -------------------------------------------------------------
  'login.form': { page: 'primeros-pasos/ingresar', label: 'Formulario de ingreso' },

  // --- Panel ---------------------------------------------------------------
  'dashboard.page': { page: 'panel', label: 'Panel' },
  'dashboard.partner': {
    page: 'panel',
    anchor: 'elegir-partner',
    label: 'Selector de partner',
  },
  'dashboard.refresh': {
    page: 'panel',
    anchor: 'refrescar',
    label: 'Refresco del panel',
  },
  'dashboard.alerts': { page: 'panel', anchor: 'alertas', label: 'Alertas activas' },
  'dashboard.accounts': { page: 'panel', anchor: 'cuentas', label: 'Cuentas verificadas' },
  'dashboard.metrics': {
    page: 'panel',
    anchor: 'indicadores',
    label: 'Indicadores principales',
  },
  'dashboard.plans': {
    page: 'panel',
    anchor: 'por-plan',
    label: 'Suscripciones por plan',
  },

  // --- Usuarios ------------------------------------------------------------
  'users.page': { page: 'usuarios/buscar', label: 'Buscador de usuarios' },
  'users.search': {
    page: 'usuarios/buscar',
    anchor: 'campo-de-busqueda',
    label: 'Campo de búsqueda',
  },
  'users.results': {
    page: 'usuarios/buscar',
    anchor: 'resultados',
    label: 'Resultados de la búsqueda',
  },
  'users.search-name': {
    page: 'usuarios/buscar',
    anchor: 'buscar-por-nombre',
    label: 'Búsqueda por nombre',
  },
  'users.pagination': {
    page: 'usuarios/buscar',
    anchor: 'pasar-de-pagina',
    label: 'Paginación de resultados',
  },

  'user.page': { page: 'usuarios/ficha', label: 'Ficha del usuario' },
  'user.summary': {
    page: 'usuarios/ficha',
    anchor: 'resumen',
    label: 'Resumen de la cuenta',
  },
  'user.identity': {
    page: 'usuarios/ficha',
    anchor: 'identidad',
    label: 'Datos de identidad',
  },
  'user.subscription': {
    page: 'usuarios/ficha',
    anchor: 'suscripcion',
    label: 'Suscripción',
  },
  'user.charge': {
    page: 'usuarios/ficha',
    anchor: 'cobro-pendiente',
    label: 'Cobro pendiente de la suscripción',
  },
  'user.pending': {
    page: 'usuarios/ficha',
    anchor: 'pago-no-completado',
    label: 'Alta con el pago no completado',
  },
  'user.attempts': {
    page: 'usuarios/ficha',
    anchor: 'intentos-de-pago',
    label: 'Intentos de pago',
  },
  'user.attempts.duplicate': {
    page: 'usuarios/ficha',
    anchor: 'cobro-duplicado',
    label: 'Cobro duplicado a reintegrar',
  },
  'payments.lookup-id': {
    page: 'usuarios/ficha',
    anchor: 'id-en-la-pasarela',
    label: 'ID del pago en la pasarela',
  },
  'user.attempts.refunded': {
    page: 'usuarios/ficha',
    anchor: 'intentos-de-pago',
    label: 'Cobro duplicado ya reintegrado',
  },
  'user.devices': {
    page: 'usuarios/ficha',
    anchor: 'dispositivos',
    label: 'Dispositivos activos',
  },
  'user.payments': { page: 'usuarios/ficha', anchor: 'pagos', label: 'Pagos' },
  'user.invoices': {
    page: 'usuarios/ficha',
    anchor: 'facturas',
    label: 'Facturas del usuario',
  },
  'user.coupons': { page: 'usuarios/ficha', anchor: 'cupones', label: 'Cupones canjeados' },
  'user.terms': {
    page: 'usuarios/ficha',
    anchor: 'terminos',
    label: 'Términos aceptados',
  },
  'user.audit': {
    page: 'usuarios/ficha',
    anchor: 'historial',
    label: 'Historial de la cuenta',
  },
  'user.actions': { page: 'usuarios/acciones', label: 'Acciones sobre el usuario' },
  'user.actions.block': {
    page: 'usuarios/acciones',
    anchor: 'bloquear',
    label: 'Bloquear la cuenta',
  },
  'user.actions.unblock': {
    page: 'usuarios/acciones',
    anchor: 'desbloquear',
    label: 'Desbloquear la cuenta',
  },
  'user.actions.logout-all': {
    page: 'usuarios/acciones',
    anchor: 'cerrar-sesiones',
    label: 'Cerrar todas las sesiones',
  },
  'user.actions.device-delete': {
    page: 'usuarios/acciones',
    anchor: 'quitar-dispositivo',
    label: 'Quitar un dispositivo',
  },
  'user.actions.hall-pass': {
    page: 'usuarios/acciones',
    anchor: 'pase-de-gracia',
    label: 'Pase de gracia',
  },
  'user.actions.email-report': {
    page: 'usuarios/acciones',
    anchor: 'investigar-correos',
    label: 'Investigar correos',
  },
  'user.actions.reconcile': {
    page: 'usuarios/acciones',
    anchor: 'reconciliar-pago',
    label: 'Reconciliar un pago',
  },
  'user.actions.reconcile.payment-id': {
    page: 'usuarios/acciones',
    anchor: 'id-de-pago-de-line',
    label: 'ID de pago de Line',
  },
  'user.actions.reconcile.results': {
    page: 'usuarios/acciones',
    anchor: 'resultados-de-reconciliar',
    label: 'Resultado de reconciliar un pago',
  },

  // --- Partners ------------------------------------------------------------
  'partners.page': { page: 'partners/lista', label: 'Lista de partners' },
  'partners.new': { page: 'partners/alta', label: 'Crear un partner' },
  'partner.page': { page: 'partners/general', label: 'Ficha del partner' },
  'partner.tabs': {
    page: 'partners/general',
    anchor: 'solapas',
    label: 'Solapas de la ficha',
  },
  'partner.general': { page: 'partners/general', label: 'Datos generales' },
  'partner.general.ttl': {
    page: 'partners/general',
    anchor: 'duracion-de-sesion',
    label: 'Duración de las sesiones',
  },
  'partner.credentials': {
    page: 'partners/general',
    anchor: 'credenciales',
    label: 'Credenciales del partner',
  },
  'partner.webhook-secret': {
    page: 'partners/general',
    anchor: 'secreto-de-webhooks',
    label: 'Secreto de webhooks',
  },
  'partner.branding': { page: 'partners/branding', label: 'Branding' },
  'partner.signup': { page: 'partners/registro', label: 'Formulario de registro' },
  'partner.plans': { page: 'partners/planes', label: 'Planes del partner' },
  'partner.plans.new': {
    page: 'partners/planes',
    anchor: 'crear-un-plan',
    label: 'Nuevo plan',
  },
  'partner.clients': { page: 'partners/aplicaciones', label: 'Aplicaciones conectadas' },
  'partner.clients.new': {
    page: 'partners/aplicaciones',
    anchor: 'crear-una-aplicacion',
    label: 'Nueva aplicación',
  },
  'partner.clients.presets': {
    page: 'partners/aplicaciones',
    anchor: 'accesos-directos',
    label: 'Accesos directos',
  },
  'partner.clients.overrides': {
    page: 'partners/aplicaciones',
    anchor: 'ajustes-propios',
    label: 'Ajustes propios de la aplicación',
  },
  'partner.clients.overrides.link-max-age': {
    page: 'partners/aplicaciones',
    anchor: 'vigencia-del-enlace',
    label: 'Vigencia del enlace de la plataforma',
  },
  'partner.terms': { page: 'partners/terminos', label: 'Términos y condiciones' },
  'partner.danger': { page: 'partners/zona-critica', label: 'Zona crítica' },
  'partner.danger.switches': {
    page: 'partners/zona-critica',
    anchor: 'interruptores',
    label: 'Interruptores de funcionalidad',
  },
  'partner.danger.payments': {
    page: 'partners/zona-critica',
    anchor: 'medios-de-pago',
    label: 'Medios de pago',
  },
  'partner.danger.invoicing': {
    page: 'partners/zona-critica',
    anchor: 'facturacion',
    label: 'Facturación electrónica',
  },

  // --- Planes --------------------------------------------------------------
  'plan.page': { page: 'planes/detalle', label: 'Detalle del plan' },
  'plan.current': {
    page: 'planes/detalle',
    anchor: 'configuracion-vigente',
    label: 'Configuración vigente',
  },
  'plan.versions': {
    page: 'planes/detalle',
    anchor: 'versiones',
    label: 'Versiones del plan',
  },
  'plan.features': {
    page: 'planes/detalle',
    anchor: 'prestaciones',
    label: 'Prestaciones del plan',
  },
  'plan.tags': { page: 'planes/detalle', anchor: 'etiquetas', label: 'Etiquetas' },

  // --- Catalogo ------------------------------------------------------------
  'catalog.page': { page: 'catalogo/tipos', label: 'Catálogo' },
  'catalog.partner': {
    page: 'catalogo/tipos',
    anchor: 'elegir-partner',
    label: 'Partner del catálogo',
  },
  'catalog.types': { page: 'catalogo/tipos', label: 'Tipos del catálogo' },
  'catalog.items': { page: 'catalogo/items', label: 'Ítems del catálogo' },

  // --- Reportes y facturas -------------------------------------------------
  'reports.page': { page: 'reportes', label: 'Reportes' },
  'reports.filters': { page: 'reportes', anchor: 'filtros', label: 'Filtros del reporte' },
  'reports.export': { page: 'reportes', anchor: 'descargar', label: 'Descargar el reporte' },
  'reports.integrity': {
    page: 'reportes',
    anchor: 'integridad',
    label: 'Aviso de números no confiables',
  },
  'reports.totals': { page: 'reportes', anchor: 'totales', label: 'Totales del período' },
  'reports.accounts': {
    page: 'reportes',
    anchor: 'cuentas-por-origen',
    label: 'Cuentas por origen',
  },
  'invoices.page': { page: 'facturas', label: 'Facturas' },
  'invoices.filters': { page: 'facturas', anchor: 'filtros', label: 'Filtros de facturas' },
  'invoices.table': { page: 'facturas', anchor: 'estados', label: 'Estado de cada comprobante' },
  'invoices.retry': { page: 'facturas', anchor: 'reintentar', label: 'Reintentar una factura' },
  'invoices.resend': { page: 'facturas', anchor: 'reenviar', label: 'Reenviar por correo' },

  // --- Cobros pendientes ----------------------------------------------------
  'charges.page': { page: 'usuarios/cobros', label: 'Cobros pendientes' },
  'charges.summary': {
    page: 'usuarios/cobros',
    anchor: 'situaciones',
    label: 'Las situaciones',
  },
  'charges.duplicates': {
    page: 'usuarios/cobros',
    anchor: 'duplicados',
    label: 'Cobros duplicados a reintegrar',
  },
  'charges.mark-refunded': {
    page: 'usuarios/cobros',
    anchor: 'marcar-reintegrado',
    label: 'Marcar como reintegrado',
  },
  'charges.applied-duplicates': {
    page: 'usuarios/cobros',
    anchor: 'aplicados',
    label: 'Cobros aplicados dos veces',
  },
  'charges.refund-applied-duplicate': {
    page: 'usuarios/cobros',
    anchor: 'registrar-reintegro',
    label: 'Registrar reintegro',
  },
  'charges.unmatched': {
    page: 'usuarios/cobros',
    anchor: 'no-identificados',
    label: 'Pagos no identificados',
  },
  'charges.resolve-unmatched': {
    page: 'usuarios/cobros',
    anchor: 'resolver-no-identificado',
    label: 'Resolver un pago no identificado',
  },
  'charges.list': { page: 'usuarios/cobros', anchor: 'lista', label: 'La lista de cobros' },
  'charges.retry': {
    page: 'usuarios/cobros',
    anchor: 'reintentar',
    label: 'Reintentar cobro ahora',
  },
  'charges.bulk': {
    page: 'usuarios/cobros',
    anchor: 'en-lote',
    label: 'Reintentar toda la lista',
  },
  // --- Operacion -----------------------------------------------------------
  'jobs.page': { page: 'trabajos', label: 'Jobs y DLQ' },
  'jobs.scheduler': {
    page: 'trabajos',
    anchor: 'renovaciones',
    label: 'Últimas corridas de renovación',
  },
  'jobs.charge_errors': {
    page: 'trabajos',
    anchor: 'cobros-frenados',
    label: 'Cobros frenados por el medio de pago',
  },
  'jobs.charge_retry': {
    page: 'trabajos',
    anchor: 'cobros-frenados',
    label: 'Reintentar los cobros frenados',
  },
  'jobs.queues': { page: 'trabajos', anchor: 'colas', label: 'Estado de las colas' },
  'jobs.redrive': { page: 'trabajos', anchor: 'reprocesar', label: 'Reprocesar la DLQ' },
  'audit.page': { page: 'auditoria', label: 'Registro de auditoría' },
  'audit.filters': { page: 'auditoria', anchor: 'filtros', label: 'Filtros del registro' },
  'audit.results': { page: 'auditoria', anchor: 'resultados', label: 'Eventos registrados' },

  // --- Administracion del propio backoffice --------------------------------
  'disposable.page': { page: 'blacklist-email', label: 'Blacklist de email' },
  'disposable.add': {
    page: 'blacklist-email',
    anchor: 'agregar',
    label: 'Agregar un dominio',
  },
  'team.page': { page: 'equipo', label: 'Equipo' },
  'team.new': { page: 'equipo', anchor: 'agregar-una-cuenta', label: 'Agregar una cuenta' },
  'team.permissions': { page: 'equipo', anchor: 'permisos', label: 'Roles y permisos' },
  'coupons.page': { page: 'cupones', label: 'Cupones' },
  'coupons.new': { page: 'cupones', anchor: 'emitir', label: 'Emitir cupones' },
  'coupons.list': { page: 'cupones', anchor: 'seguimiento', label: 'Cupones emitidos' },
  'account.page': { page: 'mi-cuenta', label: 'Mi cuenta' },
  'account.profile': { page: 'mi-cuenta', anchor: 'tus-datos', label: 'Tus datos' },
  'account.password': {
    page: 'mi-cuenta',
    anchor: 'contrasena',
    label: 'Cambiar la contraseña',
  },

  // --- Equipo → Perfiles de acceso -----------------------------------------
  'team.tabs': { page: 'equipo/perfiles', label: 'Cuentas / Perfiles de acceso' },
  'profiles.page': { page: 'equipo/perfiles', label: 'Perfiles de acceso' },
  'profiles.new': {
    page: 'equipo/perfiles',
    anchor: 'crear-un-perfil',
    label: 'Nuevo perfil',
  },
  'profiles.list': {
    page: 'equipo/perfiles',
    anchor: 'la-lista',
    label: 'Lista de perfiles',
  },
  'profiles.editor': {
    page: 'equipo/perfiles',
    anchor: 'editar-y-borrar',
    label: 'Editor de perfil',
  },
  'profiles.matrix': {
    page: 'equipo/perfiles',
    anchor: 'la-matriz',
    label: 'Matriz de secciones',
  },
} satisfies Record<string, HelpTarget>;

export type HelpId = keyof typeof HELP_TARGETS;

export function resolveHelp(id: string): HelpTarget | null {
  return (HELP_TARGETS as Record<string, HelpTarget>)[id] ?? null;
}
